import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { useRawConversation, useRawConversationRef, useRegisterCallbacks, } from "./ConversationContext.js";
const ConversationInputContext = createContext(null);
/**
 * Reads from `ConversationContext` and manages microphone mute state.
 * `setMuted` calls `conversation.setMicMuted()` and updates local state.
 * Must be rendered inside a `ConversationProvider`.
 */
export function ConversationInputProvider({ children, isMuted: controlledIsMuted, onMutedChange, }) {
    const conversation = useRawConversation();
    const conversationRef = useRawConversationRef();
    const isControlled = typeof controlledIsMuted === "boolean";
    const [uncontrolledIsMuted, setUncontrolledIsMuted] = useState(false);
    const isMuted = isControlled ? controlledIsMuted : uncontrolledIsMuted;
    useRegisterCallbacks({
        onDisconnect() {
            if (!isControlled) {
                setUncontrolledIsMuted(false);
            }
        },
    });
    useEffect(() => {
        if (isControlled && conversation) {
            conversation.setMicMuted(controlledIsMuted);
        }
    }, [conversation, controlledIsMuted, isControlled]);
    const setMuted = useCallback((muted) => {
        const conversation = conversationRef.current;
        if (!conversation) {
            throw new Error("No active conversation. Call startSession() first.");
        }
        if (!isControlled) {
            conversation.setMicMuted(muted);
            setUncontrolledIsMuted(muted);
        }
        onMutedChange?.(muted);
    }, [conversationRef, isControlled, onMutedChange]);
    const value = useMemo(() => ({ isMuted, setMuted }), [isMuted, setMuted]);
    return (_jsx(ConversationInputContext.Provider, { value: value, children: children }));
}
/**
 * Returns the current microphone mute state and a function to change it.
 * Re-renders only when the mute state changes.
 *
 * Must be used within a `ConversationProvider`.
 */
export function useConversationInput() {
    const ctx = useContext(ConversationInputContext);
    if (!ctx) {
        throw new Error("useConversationInput must be used within a ConversationProvider");
    }
    return ctx;
}
//# sourceMappingURL=ConversationInput.js.map